import i18n from '@/i18n';

/** 与后端 AllowedImageFormats 对齐的用户上传白名单。 */

export const IPE_UPLOAD_ACCEPT =
  'image/jpeg,image/jpg,image/png,image/webp,.jpg,.jpeg,.png,.webp';

export const IPE_UPLOAD_FORMAT_HINT = '仅支持 JPG、JPEG、PNG、WebP 格式';

export const IPE_UPLOAD_FORMAT_HINT_WITH_LIMIT =
  '单张不超过 50MB · 仅支持 JPG、JPEG、PNG、WebP 格式';

export const IPE_UPLOAD_FORMAT_HINT_WITH_DIMENSION =
  `${IPE_UPLOAD_FORMAT_HINT_WITH_LIMIT} · 尺寸不超过 4096×4096`;

export const IPE_UPLOAD_UNSUPPORTED_MESSAGE = '服务不支持上传该类型文件';

/** 按当前界面语言取上传提示（渲染时调用） */
export function getIpeUploadFormatHint() {
  return i18n.t('workbench:upload.formatsOnly');
}

export function getIpeUploadFormatHintWithLimit() {
  return i18n.t('workbench:upload.formatsWithLimit');
}

export function getIpeUploadFormatHintWithDimension() {
  return i18n.t('workbench:upload.formatsWithDimension', {
    limit: getIpeUploadFormatHintWithLimit(),
  });
}

export function getIpeUploadUnsupportedMessage() {
  return i18n.t('workbench:upload.unsupported');
}

const ALLOWED_MIME = new Set(['image/jpeg', 'image/jpg', 'image/png', 'image/webp']);
const ALLOWED_EXTENSIONS = new Set(['jpg', 'jpeg', 'png', 'webp']);

export function readUploadFileExtension(fileName) {
  const base = String(fileName || '').trim();
  const idx = base.lastIndexOf('.');
  if (idx < 0 || idx === base.length - 1) {
    return '';
  }
  return base.slice(idx + 1).toLowerCase();
}

/** 与后端 ImageMimeValidator 对齐：MIME → 上传扩展名 */
export function uploadExtensionForMime(mime) {
  const normalized = String(mime || '').trim().toLowerCase();
  if (!normalized) return '';
  if (normalized.includes('jpeg') || normalized === 'image/jpg') return 'jpg';
  if (normalized.includes('png')) return 'png';
  if (normalized.includes('webp')) return 'webp';
  return '';
}

export function uploadExtensionForFileName(fileName) {
  const ext = readUploadFileExtension(fileName);
  if (ext === 'jpg' || ext === 'jpeg') return 'jpg';
  if (ext === 'png') return 'png';
  if (ext === 'webp') return 'webp';
  return '';
}

/** 从 data URL 头部解析 MIME（fetch 后 blob.type 可能为空） */
export function parseDataUrlMime(dataUrl) {
  const match = String(dataUrl || '').trim().match(/^data:(image\/[a-zA-Z0-9+.-]+);/);
  return match ? match[1].toLowerCase() : '';
}

/** 根据文件头 magic 推断 MIME（与后端 ImageMagicSniffer 对齐） */
export function sniffImageMimeFromBytes(head) {
  const bytes = head instanceof Uint8Array ? head : new Uint8Array(head || []);
  if (bytes.length >= 3 && bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) {
    return 'image/jpeg';
  }
  if (bytes.length >= 4 && bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4e && bytes[3] === 0x47) {
    return 'image/png';
  }
  if (
    bytes.length >= 12
    && bytes[0] === 0x52 && bytes[1] === 0x49 && bytes[2] === 0x46 && bytes[3] === 0x46
    && bytes[8] === 0x57 && bytes[9] === 0x45 && bytes[10] === 0x42 && bytes[11] === 0x50
  ) {
    return 'image/webp';
  }
  return '';
}

export async function sniffBlobImageMime(blob) {
  if (!blob || typeof blob.slice !== 'function') return '';
  try {
    const head = new Uint8Array(await blob.slice(0, 12).arrayBuffer());
    return sniffImageMimeFromBytes(head);
  } catch {
    return '';
  }
}

/**
 * 综合 blob.type / data URL / 文件名 / magic 推断上传 MIME。
 * @param {{ blob?: Blob, fileName?: string, dataUrlHint?: string }} hints
 */
export async function resolveUploadMime(hints = {}) {
  const blob = hints.blob;
  const declared = String(blob?.type || '').trim().toLowerCase();
  if (declared && ALLOWED_MIME.has(declared)) return declared;

  const fromDataUrl = parseDataUrlMime(hints.dataUrlHint);
  if (fromDataUrl && ALLOWED_MIME.has(fromDataUrl)) return fromDataUrl;

  const extMime = uploadExtensionForFileName(hints.fileName || (blob instanceof File ? blob.name : ''));
  if (extMime) {
    const mapped = extMime === 'jpg' ? 'image/jpeg' : extMime === 'png' ? 'image/png' : 'image/webp';
    if (ALLOWED_MIME.has(mapped)) return mapped;
  }

  const sniffed = await sniffBlobImageMime(blob);
  if (sniffed && ALLOWED_MIME.has(sniffed)) return sniffed;

  return declared;
}

/** 生成与 MIME 一致的上传文件名（避免 JPEG 内容 + .png 扩展名被后端拒绝） */
export function defaultUploadFileNameForMime(mime, timestamp = Date.now()) {
  const ext = uploadExtensionForMime(mime) || 'jpg';
  return `upload-${timestamp}.${ext}`;
}

/**
 * 确保 File 扩展名与 MIME 一致（后端 magic + ext 交叉校验）。
 * @returns {File}
 */
export function normalizeUploadFileName(file, mimeOverride = '') {
  if (!file || !(file instanceof File)) return file;
  const mime = String(mimeOverride || file.type || '').trim().toLowerCase();
  const expectedExt = uploadExtensionForMime(mime) || uploadExtensionForFileName(file.name);
  if (!expectedExt) return file;
  const resolvedMime = mime || (expectedExt === 'jpg' ? 'image/jpeg' : expectedExt === 'png' ? 'image/png' : 'image/webp');
  const currentExt = readUploadFileExtension(file.name);
  if (currentExt === expectedExt && (!mime || String(file.type || '').toLowerCase() === resolvedMime)) {
    return file;
  }
  const baseName = (file.name || 'upload').replace(/\.[^.]+$/, '') || 'upload';
  return new File([file], `${baseName}.${expectedExt}`, {
    type: resolvedMime,
    lastModified: file.lastModified,
  });
}

/**
 * 由 Blob 构造可 multipart 上传的 File（MIME / 扩展名 / magic 一致）。
 * @param {Blob} blob
 * @param {{ filename?: string, dataUrlHint?: string }} [options]
 */
export async function buildUploadFileFromBlob(blob, options = {}) {
  if (!blob) {
    throw new Error('无效图片数据');
  }
  const fileName = options.filename || (blob instanceof File ? blob.name : '');
  const mime = await resolveUploadMime({
    blob,
    fileName,
    dataUrlHint: options.dataUrlHint,
  });
  const name = fileName || defaultUploadFileNameForMime(mime);
  const base = blob instanceof File ? blob : new File([blob], name, { type: mime || blob.type || '' });
  return normalizeUploadFileName(base, mime);
}

export function isAllowedUploadMime(mime) {
  const normalized = String(mime || '').trim().toLowerCase();
  if (!normalized) {
    return false;
  }
  return ALLOWED_MIME.has(normalized);
}

export function isAllowedUploadFile(file) {
  return validateIpeUploadFile(file) == null;
}

/**
 * @returns {string|null} 错误文案；通过则返回 null
 */
export function validateIpeUploadFile(file) {
  if (!file) {
    return getIpeUploadUnsupportedMessage();
  }

  const mime = String(file.type || '').trim().toLowerCase();
  const ext = readUploadFileExtension(file.name);

  if (mime && !ALLOWED_MIME.has(mime)) {
    return getIpeUploadUnsupportedMessage();
  }

  if (ext && !ALLOWED_EXTENSIONS.has(ext)) {
    return getIpeUploadUnsupportedMessage();
  }

  if (!mime && !ext) {
    return getIpeUploadUnsupportedMessage();
  }

  return null;
}

/** @returns {{ valid: File[], rejected: File[] }} */
export function validateIpeUploadFiles(files) {
  const list = Array.from(files || []);
  const valid = [];
  const rejected = [];
  list.forEach((file) => {
    if (validateIpeUploadFile(file)) {
      rejected.push(file);
    } else {
      valid.push(file);
    }
  });
  return { valid, rejected };
}

export function filterAllowedUploadFiles(files) {
  return validateIpeUploadFiles(files).valid;
}
